import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Grid, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import CommonTable from "components/commonTable/CommonTable";
import Loader from "components/loader/Loader";
import useModal from "hooks/useModal";
import GenerateInvoiceModal from "pages/operation/operationModals/GenerateInvoiceModal";
import BillGenerateLink from "./BillGenerateLink";
import { roleAndPermissionEnum } from "utils/rolesPermissionsEnum";
import { handlePermissions } from "utils/utils";

function BillingFlowTab(props) {
  const navigate = useNavigate();

  const [selectedCheckRows, setSelectedCheckRows] = useState([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [tripDetails, setTripDetails] = useState(false);
  const { open: generateInvoiceModal, toggle: setGenerateInvoiceModal } = useModal();
  const { handleChangePage, handleChangeRowsPerPage } = props;

  const handleTabChange = (event, newValue) => {
    props?.handleTab(newValue);
    setSelectedCheckRows([]);
    setSelectAllChecked(false);
  };

  const handleCheckboxChange = (row, body) => {
    if (selectedCheckRows.includes(row)) {
      // If the row is already selected, unselect it
      setSelectedCheckRows(
        selectedCheckRows.filter((selectedRow) => selectedRow !== row)
      );
      setSelectAllChecked(false); // Uncheck the header checkbox
    } else {
      // If the row is not selected, select it
      setSelectedCheckRows([...selectedCheckRows, row]);

      // Check if all rows are selected
      const allRowsSelected = body.every((item) =>
        selectedCheckRows.includes(item)
      );
      setSelectAllChecked(allRowsSelected); 
    }
  };

  const handleSelectAllCheckboxChange = (data) => {
    if (selectAllChecked) {
      // If the header checkbox is checked, uncheck all checkboxes
      setSelectedCheckRows([]);
    } else {
      // If the header checkbox is unchecked, check all checkboxes
      setSelectedCheckRows(data);
    }
    setSelectAllChecked(!selectAllChecked);
  };

  const onClickMoreIcon = (data, type) => {
    setTripDetails(data);
    if (type === "Regenerate") {
      setGenerateInvoiceModal();
    } else if (type === "Duty Slip") {
      handleDutySlipNavigate(data?.tripUuid);
    } else if (type === "Proforma Invoice") {
      handlePerformaNavigate(data?.tripUuid);
    } else if (type === "Consolidated Invoice") {
      navigate(
        `/operation/billing/consolidate-invoice?tripId=${data?.id}&companyId=${props?.billingFilter?.parentCompanyName?.value}&companyName=${props?.billingFilter?.parentCompanyName?.label}&cityId=${props?.billingFilter?.city?.value}&cityName=${props?.billingFilter?.city?.label}&status=${props?.tab}&page=${props?.page}&pageRowSize=${props?.rowsPerPage}&startDate=${props?.queryParams?.fromDate}&endDate=${props?.queryParams?.toDate}`
      );
    } else if(type === "Individual Invoice"){
      navigate(
        `/operation/billing/individual-invoice?tripId=${data?.tripId}&companyId=${props?.billingFilter?.parentCompanyName?.value}&companyName=${props?.billingFilter?.parentCompanyName?.label}&cityId=${props?.billingFilter?.city?.value}&cityName=${props?.billingFilter?.city?.label}&status=${props?.tab}&page=${props?.page}&pageRowSize=${props?.rowsPerPage}&startDate=${props?.queryParams?.fromDate}&endDate=${props?.queryParams?.toDate}&billingType=${props?.queryParams?.billingType}`
      );
    }
  };

  const onHandlePreview = (id, type) => {
    if (type === "Duty Slip") {
      handleDutySlipNavigate(id);
    } else {
      handlePerformaNavigate(id);
    }
  };

  //navigate to Duty slip
  const handleDutySlipNavigate = (id) => {
    navigate(
      `/operation/billing/duty-slip?tripId=${id}&companyId=${props?.billingFilter?.parentCompanyName?.value}&companyName=${props?.billingFilter?.parentCompanyName?.label}&cityId=${props?.billingFilter?.city?.value}&cityName=${props?.billingFilter?.city?.label}&status=${props?.tab}&page=${props?.page}&pageRowSize=${props?.rowsPerPage}&startDate=${props?.queryParams?.fromDate}&endDate=${props?.queryParams?.toDate}`
    );
  };

  //navigate to Proforma Invoice
  const handlePerformaNavigate = (id) => {
    navigate(
      `/operation/billing/performa-invoice?tripId=${id}&companyId=${props?.billingFilter?.parentCompanyName?.value}&companyName=${props?.billingFilter?.parentCompanyName?.label}&cityId=${props?.billingFilter?.city?.value}&cityName=${props?.billingFilter?.city?.label}&status=${props?.tab}&page=${props?.page}&pageRowSize=${props?.rowsPerPage}&startDate=${props?.queryParams?.fromDate}&endDate=${props?.queryParams?.toDate}`
    );
  };

  const roleAndPermissions = useSelector((state) => state?.getRolesAndPermissions?.rolesAndPermissions);
  const billingRoles = roleAndPermissionEnum.operation
  const accessForGenerate = handlePermissions(roleAndPermissions, billingRoles.moduleKey, {customAccess: billingRoles.billing.billingGenerate});

  return (
    <Box>
      <Grid container>
        <Grid item className="tabContainer" xs={12}>
          <Box className="contentBoxStyle">
            <TabContext value={props?.tab}>
              <Grid
                container
                className="tabBoxStyle mtb20"
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: '100%', overflow: 'hidden' }}
              >
                <Grid item sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', scrollBehavior: 'smooth', msOverflowStyle: 'none', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                  <TabList
                    onChange={handleTabChange}
                    aria-label="lab API tabs example"
                    TabIndicatorProps={{
                      style: { backgroundColor: "white" },
                    }}
                    sx={{
                      minWidth: 'fit-content',
                      '& .MuiTabs-flexContainer': {
                        flexWrap: 'nowrap',
                      },
                      '& .MuiTab-root': {
                        whiteSpace: 'nowrap',
                      }
                    }}
                  >
                    {props?.billingTabs?.map((tabValue, idx) => (
                      <Tab  key={idx} label={tabValue?.label} value={tabValue?.value || ""} className="tabStyle" />
                    ))}
                  </TabList>
                </Grid>
              </Grid>

              {props?.billingTabs?.map((tabValue, index) => (
                <TabPanel
                  key={index}
                  value={tabValue?.value || ""}
                  className="customTablePanel"
                >
               {!props?.billingListLoader ? (
                 <>
                   {(tabValue?.value === "pendingApproval" ||
                     tabValue?.value === "approvalForCharge") && (
                     <BillGenerateLink
                       tableValue={tabValue?.value}
                       accessForGenerate={accessForGenerate}
                       selectedCheckRows={selectedCheckRows}
                       billingListpayloadData={props?.payloadData}
                       setSelectedCheckRows={setSelectedCheckRows}
                       setSelectAllChecked={setSelectAllChecked}
                       billingFilter={props?.billingFilter}
                     />
                   )}
                   <CommonTable
                     data={props?.billingList}
                     onClickTableVendor={props?.onClickTableVendor}
                     handleCheckboxChange={handleCheckboxChange}
                     handleSelectAllCheckboxChange={
                       handleSelectAllCheckboxChange
                     }
                     accessForGenerate={accessForGenerate}
                     page={props?.page}
                     rowsPerPage={props?.rowsPerPage}
                     handleChangePage={handleChangePage}
                     handleChangeRowsPerPage={handleChangeRowsPerPage}
                     selectAllChecked={selectAllChecked}
                     onClickMoreIcon={onClickMoreIcon}
                     onHandlePreview={onHandlePreview}
                     selectedCheckRows={selectedCheckRows}
                     tabName={"operation"}
                     subTabName={"billing"}
                     viewMoreIcon={false}
                   />
                 </>
               ) : (
                 <Loader height={"60vh"} />
               )}
                </TabPanel>
              ))}
            </TabContext>
          </Box>
        </Grid>
      </Grid>
      <GenerateInvoiceModal
        open={generateInvoiceModal}
        pageType={"generateInvoice"}
        invoiceType={"regenerateInvoice"}
        setOpen={setGenerateInvoiceModal}
        onClickMoreIcon={onClickMoreIcon}
        tripDetails={tripDetails}
      />
    </Box>
  );
}

export default BillingFlowTab;
